/**
 * LaunchDarkly Provider Component
 * 
 * Wraps the application with LaunchDarkly context for feature flag access.
 * Handles SDK initialization, error states, and provides graceful fallback
 * when LaunchDarkly is unavailable.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { LDProvider, LDContext, LDOptions, useLDClient, LDFlagSet } from 'launchdarkly-react-client-sdk';
import { getLaunchDarklyConfig } from './featureFlagConfig';

interface LaunchDarklyProviderProps {
  children: React.ReactNode;
  /** Optional user context for targeting. If not provided, uses anonymous context */
  userContext?: {
    key: string;
    name?: string;
    email?: string;
    custom?: Record<string, string | boolean | number>;
  };
}

/**
 * Context to track LaunchDarkly initialization state and flags
 */
export interface LaunchDarklyState {
  isInitialized: boolean;
  isEnabled: boolean;
  error: Error | null;
  /** Flag values from LaunchDarkly (empty object when disabled) */
  flags: LDFlagSet;
}

export const LaunchDarklyStateContext = React.createContext<LaunchDarklyState>({
  isInitialized: false,
  isEnabled: false,
  error: null,
  flags: {}
});

// Stable error object to prevent unnecessary re-renders
const MISSING_CLIENT_ID_ERROR = new Error(
  'LaunchDarkly disabled: REACT_APP_LAUNCHDARKLY_CLIENT_ID is not set'
);

/**
 * Inner component that tracks LD client initialization state.
 * Does NOT subscribe to all flags to prevent unnecessary re-renders.
 */
const LDStateTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ldClient = useLDClient();
  const [initState, setInitState] = useState<{ isInitialized: boolean; isEnabled: boolean; error: Error | null }>({
    isInitialized: false,
    isEnabled: true,
    error: null
  });

  useEffect(() => {
    if (!ldClient) return;

    // Wait for initialization with 5 second timeout
    ldClient.waitForInitialization(5000)
      .then(() => {
        setInitState({
          isInitialized: true,
          isEnabled: true,
          error: null
        });
      })
      .catch((err: Error) => {
        console.error('LaunchDarkly initialization error:', err);
        setInitState({
          isInitialized: true,
          isEnabled: false,
          error: err
        });
      });
  }, [ldClient]);

  // Only provide init state, not flags - flags are subscribed to individually in useFeatureFlag
  const state = useMemo<LaunchDarklyState>(() => ({
    ...initState,
    flags: {} // Empty object - individual flags are accessed via useFlag in useFeatureFlag hook
  }), [initState]);

  return (
    <LaunchDarklyStateContext.Provider value={state}>
      {children}
    </LaunchDarklyStateContext.Provider>
  );
};

/**
 * LaunchDarkly Provider component
 * 
 * Features:
 * - Graceful fallback when LD is unavailable
 * - Anonymous user support for non-authenticated contexts
 * - Error handling for SDK initialization failures
 * - Performance optimized with streaming for real-time updates
 */
export const LaunchDarklyProvider: React.FC<LaunchDarklyProviderProps> = ({ 
  children,
  userContext 
}) => {
  const config = getLaunchDarklyConfig();

  // Build the LaunchDarkly context (user identification)
  const ldContext: LDContext = useMemo(() => {
    if (userContext) {
      return {
        ...userContext.custom,  // Spread first so reserved keys below take precedence
        kind: 'user',
        key: userContext.key,
        name: userContext.name,
        email: userContext.email,
      };
    }

    // Default anonymous context when no user is provided
    return {
      kind: 'user',
      key: 'anonymous-user',
      anonymous: true
    };
  }, [userContext]);

  // LaunchDarkly SDK options
  const ldOptions: LDOptions = useMemo(() => ({
    // Streaming enables real-time flag updates without page refresh
    streaming: true,

    // Disable sending events to reduce network traffic
    // Events track every flag evaluation, which causes excessive network calls
    sendEvents: false,

    // Disable goal tracking
    fetchGoals: false,
  }), []);

  // Memoize disabled state to prevent re-renders (only computed when config changes)
  const disabledState = useMemo((): LaunchDarklyState | null => {
    if (!config.isEnabled || !config.clientSideId) {
      const clientId = config.clientSideId;
      return {
        isInitialized: true,
        isEnabled: false,
        error: !clientId 
          ? MISSING_CLIENT_ID_ERROR 
          : new Error(`LaunchDarkly disabled: REACT_APP_LAUNCHDARKLY_CLIENT_ID appears invalid (must be 20+ alphanumeric chars, got "${clientId}" with length ${clientId.length})`),
        flags: {}
      };
    }
    return null;
  }, [config.isEnabled, config.clientSideId]);

  // If LD is not configured, render children without LD wrapper
  if (disabledState) {
    return (
      <LaunchDarklyStateContext.Provider value={disabledState}>
        {children}
      </LaunchDarklyStateContext.Provider>
    );
  }

  return (
    <LDProvider
      clientSideID={config.clientSideId}
      context={ldContext}
      options={ldOptions}
    >
      <LDStateTracker>
        {children}
      </LDStateTracker>
    </LDProvider>
  );
};

/**
 * Hook to check LaunchDarkly initialization state
 */
export const useLaunchDarklyState = (): LaunchDarklyState => {
  return React.useContext(LaunchDarklyStateContext);
};

export default LaunchDarklyProvider;
